import type { ViewRequest } from "../shared/frames";
import type { OwnIcon } from "../shared/icons";
import type { PresenceState } from "../shared/presence";
import { attempt, quietly } from "../shared/quietly";
import { applyOp, type Op, type Rec, type UsedCollection } from "../shared/views/records";
import { answerKey, keepAnswer, keptAnswer, keptAnswers } from "./answers";
import type { DataChangeDetail, PagePost } from "./data-host";
import { forgetKept, forgetKeptUnder, keep, keptKeysUnder, readKept } from "./device-store";
import { forgetWrite, type KeptWrite, keepWrite, keptWrites } from "./kept-writes";
import { forget, readJSON, writeJSON } from "./storage";

/** What opens a view: its id, the ticket or link token, and where it opens, a chat's id or `home`, which keeps what each saw apart. */
export interface ViewCredentials {
  viewId: string;
  token: string;
  templateId: string;
  place?: string;
}
/** An element someone selected, by the selector the frame reports (docs/views.md#8-isolation), with what it says. */
export interface Selection {
  selector: string;
  label?: string;
  text?: string;
  tag?: string;
  [key: string]: unknown;
}
/** Someone with the view open, from presence: who they say they are, what they selected, and their `coast.live` state. */
export interface Participant {
  clientId: string;
  personId: string | null;
  name: string;
  color: string;
  selection: Selection | null;
  editing: boolean;
  live: unknown;
}
export interface TemplateInfo {
  id: string;
  name: string;
  description?: string;
  symbol?: string;
}
export interface Metrics {
  pingMs: number | null;
}
/** A member of the view's chat, as its object lists them. */
export interface ViewMember {
  id: string;
  name: string;
  role: string;
  /** Their icon in Coast, a picture by its path on the server, or an emoji. */
  icon?: OwnIcon | null;
}
/** What the view's object says about this connection's data (docs/views.md#2-data): its chat, its partitions, and how it writes. */
export interface DataInfo {
  /** In a chat, the chat; at the view's home, null, and `chats` the ones it reads (docs/views.md#1-views). */
  scope?: "chat" | "home";
  chat: string | null;
  chats?: string[];
  parts: string[];
  writer: {
    id: string;
    role: "admin" | "member" | null;
    viewAdmin: boolean;
    roles?: Record<string, "admin" | "member">;
  };
  members: ViewMember[];
  /** The start of the links `coast.link` hands out, `https://coast.ws/c/<chat>/<view>`; null in a link view. */
  links?: string | null;
  /** Other views' records this view's manifest names in `uses`, by `view/collection`, read-only, as they were when it opened. */
  uses?: Record<string, UsedCollection>;
  /** A question's view (docs/agent.md#asking-you), which answers its question with `coast.respond`. */
  asked?: true;
  /** A view posted in a message, opened from it, whose members respond with `coast.respond` (docs/views.md#15-views-in-messages). */
  posted?: true;
}
/** The outcome of a device's write: saved, or saved with some records put back because a rule refused them. */
export interface DataAck {
  revision: number;
  refused: { collection: string; id: string }[];
}
/** This device's copy of a partition: the rows the view's object has sent, by collection and id, at the revision it has them. */
export interface PartCopy {
  revision: number;
  rows: Record<string, Record<string, Rec>>;
}
/** One changed row as the view's object sends it: the record, or null once it is removed. */
export interface RowChange {
  collection: string;
  id: string;
  rec: Rec | null;
}
/** A write not yet answered: its operation, kept on the device until the view's object answers it. */
interface Outgoing extends KeptWrite {
  sent: boolean;
  resolve?: (ack: DataAck) => void;
}

/** Set once this device's storage has moved to views' names (`moveToViews`). */
const MOVED = "coast:moved-to-views";

/**
 * Apps and rooms became views on 2026-10-17 (docs/outline.md#9-naming), and this device's storage follows, once: a
 * kept write to a view's own partition names it `view`, not `app`; the copies of partitions and of what each object
 * said about its data go, since they name `app` and their records' authors `app:<id>`, and are fetched again; the
 * sources go, so no page runs on the renamed page API before its rewritten source comes; and the link views saved under
 * rooms' names go. It runs again until IndexedDB answers. It can go once every device has opened a view since
 * 2026-10-17.
 */
export async function moveToViews(): Promise<void> {
  if (readJSON(MOVED)) return;
  const keys = await keptKeysUnder("outbox:");
  if (!keys) return;
  const renamed = (write: Partial<KeptWrite> | null) => (write?.part === "app" ? { ...write, part: "view" } : write);
  for (const key of keys) {
    // A view's writes kept as one list before 2026-10-16, which `start` takes up, are renamed too.
    const kept = await readKept<Partial<KeptWrite> | Partial<KeptWrite>[]>(key);
    const next = Array.isArray(kept) ? kept.map(renamed) : renamed(kept);
    if (JSON.stringify(next) !== JSON.stringify(kept)) await keep(key, next);
  }
  await forgetKeptUnder("data:");
  const stale = (key: string) => key.startsWith("coast:data:") || key.startsWith("coast:source:");
  const keysNow = attempt(() => Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index)));
  for (const key of keysNow ?? []) if (key && stale(key)) forget(key);
  for (const key of ["coast:rooms", "coast:room-history", "coast:last-room", "coast:scenes"]) forget(key);
  writeJSON(MOVED, "2026-10-17");
}

/**
 * A view's connection to its object (docs/views.md#2-data): its source, the people present, its data on this device,
 * and the writes, queries, actions, and posts that go through the object.
 */
export class ViewConnection extends EventTarget {
  /** This connection's id among the people present, from the object's hello; empty until then. */
  private actorId = "";
  /** This connection's presence, sent whole when it changes, and everyone else's by their connection id (`shared/presence.ts`). */
  private readonly local: PresenceState;
  private readonly others = new Map<string, PresenceState>();
  /** The view's source at its revision, as its object has them (docs/views.md#7-editing), kept on this device so the view shows at once next time. */
  source = "";
  revision = 0;
  readonly metrics: Metrics = { pingMs: null };
  connected = false;
  /** Whether a connection failed or dropped since the last one opened: the page's `coast.connection` is offline then. */
  lost = false;
  error = "";
  offlineDirty = false;
  ready = false;
  closed = false;
  socket: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private retry = 0;
  private pingId = "";
  private pingAt = 0;
  private attempt = 0;
  private beforeUnload = () => {
    this.send({ type: "presence", state: null });
    this.socket?.close();
  };
  /** The view's data this connection reads: this device's copy of each partition (docs/views.md#2-data). */
  readonly parts = new Map<string, PartCopy>();
  data: DataInfo | null = null;
  /** Partitions whose data is here: synced with the view's object, or loaded from this device after an earlier visit. */
  readonly readyParts = new Set<string>();
  /**
   * Writes not yet answered, in order, kept on the device until the view's object answers them (kept-writes.ts): those
   * made here, and those the device's other pages on the view were waiting on when this one started.
   */
  private outbox: Outgoing[] = [];
  /** How many writes this page has made, which orders those made in the same millisecond. */
  private written = 0;
  private requests = new Map<string, (message: Record<string, any>) => void>();
  /** Messages a page asked to send before the connection opened, such as its first queries; they go once it does. */
  private waiting: Array<() => void> = [];
  /** The last `coast.live.send` seen from each other connection, so each is delivered once. */
  private signals = new Map<string, number>();
  private signalSeq = 0;
  /** Where this device keeps what the view's object said about its data, for each place the view opens. */
  private get dataKey() {
    return `coast:data:${this.credentials.viewId}${this.credentials.place ? `:${this.credentials.place}` : ""}`;
  }
  /** Where this device keeps the view's source, the same in every place it opens. */
  private get sourceKey() {
    return `coast:source:${this.credentials.viewId}`;
  }
  /** Where this device keeps its copy of a partition. */
  private partKey(part: string) {
    return `data:${this.credentials.viewId}:${part}`;
  }
  /** This connection among the people present, as the view's object names it. */
  get clientID() {
    return this.actorId;
  }
  constructor(
    readonly credentials: ViewCredentials,
    readonly identity: { id: string; name: string; color: string },
  ) {
    super();
    const kept = readJSON<{ source: string; revision: number }>(this.sourceKey);
    if (kept && typeof kept.source === "string" && typeof kept.revision === "number") {
      this.source = kept.source;
      this.revision = kept.revision;
    }
    this.local = { user: identity, selection: null, editing: false };
    window.addEventListener("beforeunload", this.beforeUnload);
  }
  get pendingCount() {
    return this.outbox.length || (this.offlineDirty ? 1 : 0);
  }
  get participants(): Participant[] {
    const of = (clientId: string, state: PresenceState): Participant => {
      const user = (state.user && typeof state.user === "object" ? state.user : {}) as Record<string, unknown>;
      const selection = state.selection as Selection | null | undefined;
      return {
        clientId,
        personId: typeof user.id === "string" ? user.id : null,
        name: String(user.name || "Someone").slice(0, 40),
        color: /^#[\da-f]{6}$/i.test(String(user.color)) ? (user.color as string) : "#a4c6a2",
        selection: typeof selection?.selector === "string" && selection.selector ? selection : null,
        editing: Boolean(state.editing),
        live: state.live ?? null,
      };
    };
    return [of(this.actorId, this.local), ...[...this.others].map(([id, state]) => of(id, state))];
  }
  changed() {
    this.dispatchEvent(new Event("status"));
  }
  async start() {
    if (this.closed) return;
    await moveToViews();
    if (this.closed) return;
    // Until 2026-10-14 the view's code and data were Yjs documents on this device, with the template beside them; what
    // is left of them goes.
    const viewId = this.credentials.viewId;
    attempt(() => {
      localStorage.removeItem(`coast:template:${viewId}`);
      localStorage.removeItem(`coast:baseline:${viewId}`);
      void indexedDB.databases?.().then((databases) => {
        for (const { name } of databases)
          if (name === `coast-scene-v1:${viewId}` || name?.startsWith(`coast-data-v1:${viewId}:`))
            indexedDB.deleteDatabase(name);
      }, quietly);
    });
    // Until 2026-10-16 each page kept its unanswered writes as one list, which another page on the view could write
    // over; a list left behind is kept as its writes.
    const listed = await readKept<Pick<KeptWrite, "id" | "part" | "op">[]>(`outbox:${viewId}`);
    if (Array.isArray(listed)) {
      await Promise.all(listed.map((write, n) => keepWrite(viewId, { ...write, at: 0, n })));
      await forgetKept(`outbox:${viewId}`);
    }
    // After an earlier visit the view's data opens from this device at once, before its object answers, with the writes
    // made while there was no connection waiting to go up.
    const kept = await keptWrites(viewId);
    if (this.closed) return;
    this.outbox = kept.map((write) => ({ ...write, sent: false }));
    const cached = readJSON<DataInfo>(this.dataKey);
    if (cached && Array.isArray(cached.parts)) await this.openParts(cached, true);
    if (this.closed) return;
    // A view this device has opened before shows at once and connects while the source comes: a newer revision
    // replaces the page. Only a first visit waits for the source.
    const known = this.source.length > 0;
    if (known) this.open();
    // Opened before its ticket came, as the Coast app opens a view it has had before, it shows from this device and
    // waits for the ticket to reach its object (`useTicket`).
    if (!this.credentials.token)
      await new Promise<void>((resolve) => {
        this.ticketArrived = resolve;
      });
    if (this.closed) return;
    try {
      await this.refresh();
    } catch (error) {
      this.error = error instanceof Error ? error.message : String(error);
      if (!this.source.length) throw error;
    }
    if (!known) this.open();
  }
  /**
   * The view's source as its object has it now, when it differs from this device's: at the start, when the object's
   * hello names another revision, and after an edit (`view.reload`). The page follows the `source` event.
   */
  private async refresh() {
    const response = await fetch(`/api/views/${this.credentials.viewId}/source`, {
      headers: { Authorization: `Bearer ${this.credentials.token}` },
      signal: AbortSignal.timeout(12000),
    });
    if (!response.ok) {
      let reason = `The view could not be opened (${response.status})`;
      const body: unknown = await response.json().catch(quietly);
      if (body && typeof body === "object" && "error" in body && typeof body.error === "string") reason = body.error;
      if (response.status === 401 || response.status === 403 || response.status === 404)
        throw new Error(`${reason}. Open it again from its chat or its link, or start a new view.`);
      throw new Error(reason);
    }
    const current = (await response.json()) as { templateId?: unknown; source?: unknown; revision?: unknown };
    if (this.closed) return;
    if (typeof current.templateId === "string" && current.templateId) this.credentials.templateId = current.templateId;
    if (typeof current.source !== "string" || typeof current.revision !== "number") return;
    if (current.source === this.source && current.revision === this.revision) return;
    this.source = current.source;
    this.revision = current.revision;
    writeJSON(this.sourceKey, { source: this.source, revision: this.revision });
    this.dispatchEvent(new Event("source"));
    this.changed();
  }
  /** The ticket for a view opened before it came: the connection fetches what it missed and connects. */
  useTicket(token: string) {
    if (this.closed || this.credentials.token || !/^[\w-]{43}$/.test(token)) return;
    this.credentials.token = token;
    this.ticketArrived?.();
    this.ticketArrived = undefined;
    if (this.ready) this.connect();
  }
  private ticketArrived?: () => void;
  private open() {
    if (this.closed || this.ready) return;
    this.ready = true;
    this.dispatchEvent(new Event("ready"));
    this.connect();
  }
  connect() {
    // A view still waiting for its ticket connects once it comes.
    if (
      this.closed ||
      !this.credentials.token ||
      this.socket?.readyState === WebSocket.OPEN ||
      this.socket?.readyState === WebSocket.CONNECTING
    )
      return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.error = "";
    this.changed();
    const attempt = ++this.attempt;
    const url = new URL(`/api/views/${this.credentials.viewId}/ws`, location.href);
    url.protocol = location.protocol === "https:" ? "wss:" : "ws:";
    // In a link view, the device's own id names its person, so what it writes stays its own.
    const person = /^[A-Za-z0-9_-]{8,64}$/.test(this.identity.id) ? [`coast.person.${this.identity.id}`] : [];
    const socket = new WebSocket(url, ["coast.v1", `coast.token.${this.credentials.token}`, ...person]);
    this.socket = socket;
    socket.addEventListener("open", () => {
      if (this.closed || attempt !== this.attempt) return socket.close();
      this.connected = true;
      this.lost = false;
      this.retry = 0;
      this.error = "";
      this.sendPresence();
      for (const flush of this.waiting.splice(0)) flush();
      // Quiet sockets can hibernate server-side; a measured heartbeat also finds dead connections.
      this.pingTimer = setInterval(() => this.ping(), 25000);
      this.ping();
      this.changed();
    });
    socket.addEventListener("message", (event) => {
      if (this.closed || attempt !== this.attempt || typeof event.data !== "string") return;
      try {
        this.receive(JSON.parse(event.data));
      } catch (error) {
        this.error = `A message from the view could not be applied: ${error instanceof Error ? error.message : error}`;
        this.changed();
      }
    });
    socket.addEventListener("close", () => {
      if (this.closed || attempt !== this.attempt) return;
      this.connected = false;
      this.lost = true;
      this.pingId = "";
      // What was sent and not answered goes again on the next connection; everyone else is out of sight until then.
      for (const entry of this.outbox) entry.sent = false;
      if (this.outbox.length) this.offlineDirty = true;
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.others.clear();
      this.signals.clear();
      this.dispatchEvent(new Event("presence"));
      this.changed();
      if (!this.closed)
        this.reconnectTimer = setTimeout(
          () => this.connect(),
          Math.min(15000, 750 * 2 ** this.retry++) + Math.random() * 500,
        );
    });
    socket.addEventListener("error", () => {
      if (this.closed || attempt !== this.attempt) return;
      this.error = "Connection interrupted; local changes will sync when reconnected.";
      this.changed();
    });
  }
  private receive(message: Record<string, any>) {
    if (message.type === "presence") {
      if (Array.isArray(message.all)) for (const entry of message.all) this.arrive(entry?.client, entry?.state, true);
      else this.arrive(message.client, message.state, false);
      this.dispatchEvent(new Event("presence"));
    } else if (message.type === "pong") {
      if (message.id === this.pingId) {
        this.metrics.pingMs = performance.now() - this.pingAt;
        this.pingId = "";
      }
      this.changed();
    } else if (message.type === "hello") {
      if (typeof message.actorId === "string") this.actorId = message.actorId;
      // In a chat, the ticket says who this is; the page sees that name.
      if (message.member && typeof message.member.id === "string" && typeof message.member.name === "string") {
        this.identity.id = message.member.id;
        this.rename(message.member.name);
        this.dispatchEvent(new Event("identity"));
      }
      if (message.data)
        void this.openParts(message.data as DataInfo, false)
          .then(() => this.takeUpKept())
          .then(() => {
            if (!this.closed) this.flush();
          });
      // The source changed while this device was away, or never came: the view's object has the current one.
      if (typeof message.revision === "number" && (message.revision !== this.revision || !this.source))
        this.refresh().catch(quietly);
      this.changed();
    } else if (message.type === "view.reload") {
      // An edit: everyone with the view open gets the new source, and the page is built again.
      this.refresh().catch(quietly);
    } else if (message.type === "data.sync" || message.type === "data.update") {
      this.take(message);
    } else if (message.type === "members") {
      // Someone joined or left one of its chats, or took a new name or icon: the page's `coast.people` follows.
      if (!this.data || !Array.isArray(message.members)) return;
      this.data = { ...this.data, members: message.members };
      writeJSON(this.dataKey, this.data);
      this.dispatchEvent(new Event("data-info"));
    } else if (message.type === "data.uses") {
      if (!this.data || !message.uses || typeof message.uses !== "object") return;
      this.data = { ...this.data, uses: message.uses };
      writeJSON(this.dataKey, this.data);
      this.dispatchEvent(new CustomEvent("data", { detail: { uses: true } }));
    } else if (message.type === "data.ack") {
      // The object's answer to a write made here, with the rows it changed, before the write comes off the device.
      if (Array.isArray(message.rows)) this.take(message);
      this.answered(message.id, { revision: message.revision ?? -1, refused: message.refused || [] });
      this.changed();
    } else if (
      message.type === "data.history" ||
      message.type === "query.result" ||
      message.type === "action.result" ||
      message.type === "post.result" ||
      message.type === "respond.result"
    ) {
      const resolve = this.requests.get(message.id);
      this.requests.delete(message.id);
      resolve?.(message);
    } else if (message.type === "error") {
      this.error = message.text || message.error || message.message || "The view refused a change.";
      // A write the object could not take at all comes off the device, or it would go up on every connection; a
      // request it could not take fails with what it said, rather than waiting out its time.
      if (message.id) {
        this.answered(message.id, { revision: -1, refused: [] });
        const resolve = this.requests.get(message.id);
        this.requests.delete(message.id);
        resolve?.({ ...message, error: this.error });
      }
      this.dispatchEvent(new CustomEvent("message", { detail: { type: "error", text: this.error } }));
      this.changed();
    } else {
      this.dispatchEvent(new CustomEvent("message", { detail: message }));
      this.changed();
    }
  }
  /** Rows the view's object sends for a partition: everything, when the copy starts again, or the ones that changed. */
  private take(message: { part?: unknown; rows?: unknown; revision?: unknown; full?: unknown }) {
    const copy = typeof message.part === "string" ? this.parts.get(message.part) : undefined;
    if (!copy || !Array.isArray(message.rows) || typeof message.revision !== "number") return;
    if (message.full) copy.rows = {};
    for (const change of message.rows as RowChange[]) {
      if (!change || typeof change.collection !== "string" || typeof change.id !== "string") continue;
      copy.rows[change.collection] ??= {};
      const rows = copy.rows[change.collection]!;
      if (change.rec) rows[change.id] = change.rec;
      else delete rows[change.id];
    }
    copy.revision = Math.max(copy.revision, message.revision);
    void keep(this.partKey(message.part as string), copy);
    this.readyParts.add(message.part as string);
    const detail: DataChangeDetail = message.full
      ? { part: message.part as string }
      : {
          part: message.part as string,
          records: (message.rows as RowChange[]).flatMap((change) =>
            change && typeof change.collection === "string" && typeof change.id === "string"
              ? [{ collection: change.collection, id: change.id }]
              : [],
          ),
        };
    this.dispatchEvent(new CustomEvent("data", { detail }));
  }
  /** The object's answer to a write made here: it comes off the device, and whoever waits for it hears. */
  private answered(id: unknown, ack: DataAck) {
    const index = this.outbox.findIndex((entry) => entry.id === id);
    if (index < 0) return;
    const [entry] = this.outbox.splice(index, 1);
    if (entry) void forgetWrite(this.credentials.viewId, entry.id);
    entry?.resolve?.(ack);
    if (!this.outbox.length) this.offlineDirty = false;
    if (entry)
      this.dispatchEvent(
        new CustomEvent("data", {
          detail: {
            part: entry.part,
            records: [{ collection: entry.op.collection, id: entry.op.id }],
          } satisfies DataChangeDetail,
        }),
      );
  }
  /**
   * Opens the partitions the view's object names: from this device's copy of each, before the object answers, and,
   * once it has, asking it for what changed since the revision each copy has. A partition is ready once a copy of it is
   * here.
   */
  private async openParts(info: DataInfo, cached: boolean) {
    this.data = info;
    if (!cached) writeJSON(this.dataKey, info);
    for (const part of info.parts) {
      if (!this.parts.has(part)) {
        const kept = await readKept<PartCopy>(this.partKey(part));
        if (this.closed) return;
        const sound = kept && typeof kept.revision === "number" && kept.rows && typeof kept.rows === "object";
        this.parts.set(part, sound ? kept : { revision: 0, rows: {} });
        if (sound) {
          this.readyParts.add(part);
          this.dispatchEvent(new CustomEvent("data", { detail: { part } }));
        }
      }
      if (!cached) this.send({ type: "data.sync", part, revision: this.parts.get(part)!.revision });
    }
    this.dispatchEvent(new Event("data-info"));
  }
  /**
   * Takes up the writes the device's other pages on this view left unanswered, such as one made offline on a section's
   * screen that has since closed, so they go up with this page's (kept-writes.ts). Writes to partitions this connection
   * doesn't read belong to a page elsewhere, as the view's home's do to a chat's page, and stay kept for it.
   */
  private async takeUpKept() {
    const kept = await keptWrites(this.credentials.viewId);
    if (this.closed) return;
    const held = new Set(this.outbox.map((entry) => entry.id));
    const left = kept.filter((write) => !held.has(write.id));
    this.outbox = [...this.outbox, ...left.map((write) => ({ ...write, sent: false }))]
      .filter((entry) => this.parts.has(entry.part))
      .sort((a, b) => a.at - b.at || a.n - b.n);
    if (left.length) this.dispatchEvent(new CustomEvent("data", { detail: {} }));
  }
  /** Sends the writes made while there was no connection, in order; each stays here until the object answers it. */
  private flush() {
    for (const entry of this.outbox)
      if (!entry.sent) entry.sent = this.send({ type: "data.update", id: entry.id, part: entry.part, op: entry.op });
  }
  /**
   * A write to a partition: kept on this device until the view's object answers it, so the page shows it at once and it
   * goes up when the connection allows, and resolved with the object's answer: saved, or with the records a rule
   * refused. Offline it resolves at once, with revision -1, and the write goes up when the device reconnects.
   */
  writeData(part: string, op: Op): Promise<DataAck> {
    if (!this.parts.has(part)) return Promise.reject(new Error("That partition is not open."));
    const entry: Outgoing = { id: crypto.randomUUID(), part, op, at: Date.now(), n: this.written++, sent: false };
    this.outbox.push(entry);
    void keepWrite(this.credentials.viewId, { id: entry.id, part, op, at: entry.at, n: entry.n });
    this.dispatchEvent(
      new CustomEvent("data", {
        detail: { part, records: [{ collection: op.collection, id: op.id }] } satisfies DataChangeDetail,
      }),
    );
    return new Promise<DataAck>((resolve) => {
      entry.resolve = resolve;
      entry.sent = this.send({ type: "data.update", id: entry.id, part, op });
      if (!entry.sent) {
        entry.resolve = undefined;
        this.offlineDirty = true;
        resolve({ revision: -1, refused: [] });
      }
      this.changed();
    });
  }
  /** A partition's records of a collection as this device sees them: the object's, with this device's unanswered writes laid over. */
  rows(part: string, collection: string): Rec[] {
    const copy = this.parts.get(part);
    if (!copy) return [];
    let list = Object.values(copy.rows[collection] ?? {});
    for (const entry of this.outbox) {
      if (entry.part !== part || entry.op.collection !== collection) continue;
      const at = list.findIndex((rec) => rec.id === entry.op.id);
      const after = attempt(() => applyOp(at >= 0 ? list[at]! : null, entry.op).after);
      // A write that can't apply changes nothing here either; the object will say so.
      if (after === undefined) continue;
      if (after === null) list = at >= 0 ? list.filter((_, index) => index !== at) : list;
      else list = at >= 0 ? list.map((rec, index) => (index === at ? after : rec)) : [...list, after];
    }
    return list;
  }
  /** The collections a partition holds, this device's unanswered writes included. */
  collections(part: string): string[] {
    const names = new Set(Object.keys(this.parts.get(part)?.rows ?? {}));
    for (const entry of this.outbox) if (entry.part === part) names.add(entry.op.collection);
    return [...names];
  }
  /** This connection's `coast.live` state, which everyone with the view open sees until it changes or they leave. */
  setLive(value: unknown) {
    this.present("live", value ?? null);
  }
  /**
   * Sets a field of this device's presence when it changes. Every change is sent to the object, so a view that sets its
   * `coast.live` state on each render, with the same value, would otherwise send it as often as it draws.
   */
  private present(field: string, value: unknown) {
    if (JSON.stringify(this.local[field] ?? null) === JSON.stringify(value ?? null)) return;
    this.local[field] = value ?? null;
    this.sendPresence();
    this.dispatchEvent(new Event("presence"));
  }
  private sendPresence() {
    this.send({ type: "presence", state: this.local });
  }
  /** A `coast.live.send`: delivered once to everyone with the view open now, and never saved. */
  sendLive(type: string, data: unknown) {
    this.local.signal = { type, data: data ?? null, seq: ++this.signalSeq };
    this.sendPresence();
  }
  /** Another connection's presence as the object relays it: its state, or null once it has gone. */
  private arrive(client: unknown, state: unknown, arriving: boolean) {
    if (typeof client !== "string" || client === this.actorId) return;
    if (!state || typeof state !== "object") {
      this.others.delete(client);
      this.signals.delete(client);
      return;
    }
    const first = !this.others.has(client);
    this.others.set(client, state as PresenceState);
    const signal = (state as PresenceState).signal as { type?: unknown; data?: unknown; seq?: unknown } | undefined;
    if (!signal || typeof signal.seq !== "number" || typeof signal.type !== "string") return;
    const last = this.signals.get(client);
    this.signals.set(client, signal.seq);
    // Someone who just arrived brings their last signal with them; it was for whoever was here then.
    if (first || arriving) return;
    if (last === undefined || signal.seq > last)
      this.dispatchEvent(
        new CustomEvent("live", { detail: { type: signal.type, data: signal.data ?? null, from: client } }),
      );
  }
  /** One of the view's queries (docs/views.md#14-queries-and-actions), as its object runs it for this connection's person; `keep` keeps the answer on this device. */
  query(name: string, params: Record<string, unknown>, keep = false): Promise<unknown> {
    const id = crypto.randomUUID();
    return new Promise((resolve, reject) => {
      this.requests.set(id, (message) => {
        if (message.error) return reject(Object.assign(new Error(String(message.error)), { code: "query" }));
        const person = this.person();
        if (keep && person) keepAnswer(answerKey(this.credentials.viewId, person, name, params), message.value ?? null);
        resolve(message.value ?? null);
      });
      // A page asks as it loads, often before the connection has opened: the query waits for it, for a while.
      const run = () => {
        clearTimeout(timer);
        if (!this.send({ type: "query.run", id, name, params })) fail();
      };
      const fail = () => {
        this.requests.delete(id);
        this.waiting = this.waiting.filter((entry) => entry !== run);
        reject(Object.assign(new Error("Coast is not connected. Try again."), { code: "unavailable" }));
      };
      const timer = setTimeout(fail, 15_000);
      if (this.socket?.readyState === WebSocket.OPEN) run();
      else this.waiting.push(run);
    });
  }

  /**
   * One of the view's actions (docs/views.md#14-queries-and-actions), which its object runs on the server as this
   * connection's person. Unlike a query it is never sent twice by itself: a call that can't be sent fails, and one
   * whose answer was lost stays unanswered, since the object keeps each by its id and a retry is the page's to make.
   */
  call(name: string, params: Record<string, unknown>, chat: string | null): Promise<unknown> {
    const id = crypto.randomUUID();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.requests.delete(id);
        reject(Object.assign(new Error("The action didn't answer in time."), { code: "unavailable" }));
      }, 45_000);
      this.requests.set(id, (message) => {
        clearTimeout(timer);
        if (message.error)
          return reject(
            Object.assign(new Error(String(message.error)), {
              code: "action",
              ...(typeof message.line === "number" ? { line: message.line } : {}),
            }),
          );
        resolve(message.value ?? null);
      });
      if (!this.send({ type: "action.run", id, name, params, ...(chat ? { chat } : {}) })) {
        clearTimeout(timer);
        this.requests.delete(id);
        reject(Object.assign(new Error("Coast is not connected. Try again."), { code: "unavailable" }));
      }
    });
  }

  /**
   * An update or an alert from the page (docs/views.md#6-the-page-api), which the view's object posts to the chat as
   * this connection's person via the view; it answers once the chat has it, or why it didn't.
   */
  post(post: PagePost): Promise<unknown> {
    const id = crypto.randomUUID();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.requests.delete(id);
        reject(Object.assign(new Error("The post didn't answer in time."), { code: "unavailable" }));
      }, 15_000);
      this.requests.set(id, (message) => {
        clearTimeout(timer);
        if (message.error) reject(Object.assign(new Error(String(message.error)), { code: "post" }));
        else resolve(null);
      });
      if (!this.send({ type: "post.run", id, ...post })) {
        clearTimeout(timer);
        this.requests.delete(id);
        reject(Object.assign(new Error("Coast is not connected. Try again."), { code: "unavailable" }));
      }
    });
  }

  /**
   * A question's view's answer (docs/agent.md#asking-you), with the label of what was chosen, which the view's object
   * passes to the account object of the person asked; it answers once their answer is kept, or why it wasn't, such as a
   * question already answered.
   */
  respond(value: unknown, label?: string): Promise<unknown> {
    const id = crypto.randomUUID();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.requests.delete(id);
        reject(Object.assign(new Error("The answer didn't go through in time."), { code: "unavailable" }));
      }, 15_000);
      this.requests.set(id, (message) => {
        clearTimeout(timer);
        if (message.error) reject(Object.assign(new Error(String(message.error)), { code: "respond" }));
        else resolve(null);
      });
      if (!this.send({ type: "respond.run", id, value, ...(label === undefined ? {} : { label }) })) {
        clearTimeout(timer);
        this.requests.delete(id);
        reject(Object.assign(new Error("Coast is not connected. Try again."), { code: "unavailable" }));
      }
    });
  }

  /** An error the view's page hit here, told to its object once a connection, for the agent's view_read (docs/agent.md#5-tools). */
  private reported = new Set<string>();
  reportProblem(problem: { kind: string; message: string; line?: number }) {
    const key = `${problem.kind}:${problem.message}`;
    if (this.reported.has(key) || this.reported.size >= 10) return;
    this.reported.add(key);
    // Most come as the page loads, before the connection opens; they wait for it as queries do.
    const send = () => {
      this.send({
        type: "view.problem",
        kind: problem.kind,
        message: String(problem.message).slice(0, 300),
        line: problem.line,
      });
    };
    if (this.socket?.readyState === WebSocket.OPEN) send();
    else this.waiting.push(send);
  }

  /** This device's last answer to a query with these parameters, or null. */
  cached(name: string, params: Record<string, unknown>) {
    const person = this.person();
    return person ? keptAnswer(answerKey(this.credentials.viewId, person, name, params)) : null;
  }
  /** Every answer this device kept for this view's queries, for the frame's copy. */
  answers() {
    const person = this.person();
    return person ? keptAnswers(this.credentials.viewId, person) : {};
  }
  /** Whom this connection is for: as the view's object said, or as it said on this device's last visit, before it answers now. */
  private person(): string | null {
    if (this.data) return this.data.writer.id;
    try {
      return (JSON.parse(localStorage.getItem(this.dataKey) || "null") as DataInfo | null)?.writer?.id ?? null;
    } catch {
      return null;
    }
  }

  /** A record's history from the view's object. */
  history(part: string, collection: string, record: string): Promise<unknown[]> {
    const id = crypto.randomUUID();
    return new Promise((resolve, reject) => {
      if (!this.send({ type: "data.history", id, part, collection, record }))
        return reject(new Error("History needs a connection."));
      this.requests.set(id, (message) =>
        message.error
          ? reject(new Error(String(message.error)))
          : resolve(Array.isArray(message.entries) ? message.entries : []),
      );
    });
  }

  send(message: ViewRequest) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify(message));
    return true;
  }
  ping() {
    if (this.pingId) {
      if (performance.now() - this.pingAt > 45000) this.socket?.close();
      return;
    }
    this.pingId = crypto.randomUUID();
    this.pingAt = performance.now();
    this.send({ type: "ping", id: this.pingId });
  }
  select(selection: Selection | null) {
    this.present("selection", selection);
  }
  editing(active: boolean) {
    this.present("editing", active);
  }
  rename(name: string) {
    this.identity.name = name;
    this.local.user = { ...this.identity };
    this.sendPresence();
    this.dispatchEvent(new Event("presence"));
  }
  dispose() {
    this.closed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.socket?.close();
    window.removeEventListener("beforeunload", this.beforeUnload);
  }
}
